import { IPersonalInfo, ConfigField, IOffer } from '../../../../../types';
import { ApplicationState } from '../../../../../store';

type Fields = Pick<IPersonalInfo, 'studentNo'> & Pick<IOffer, 'courses' | 'casReceived'>;

export type Visible = Record<
  | 'studentNumber'
  | 'autoCourseName'
  | 'manualCourseName'
  | 'startDate'
  | 'endDate'
  | 'courseFee'
  | 'casReceived'
  | 'isWorkPlacement',
  boolean
>;

export type Mandatory = Record<
  | 'studentNumber'
  | 'autoCourseName'
  | 'manualCourseName'
  | 'startDate'
  | 'endDate'
  | 'courseFee'
  | 'casReceived'
  | 'isWorkPlacement',
  boolean
>;

export type ItemsDisabled = Record<
  | 'studentNumber'
  | 'autoCourseName'
  | 'manualCourseName'
  | 'startDate'
  | 'endDate'
  | 'courseFee'
  | 'casReceived'
  | 'isWorkPlacement',
  boolean
>;

export function getPageStatus(
  config: ApplicationState['config'],
  fields: Fields,
  courseNotListedOrResearchStudent = false
): {
  complete: boolean;
  visible: Visible;
  mandatory: Mandatory;
} {
  const { studentNo: studentNumber, courses, casReceived } = fields;

  const {
    StudentNumber: studentNumberConfig,
    AutoCourseName: autoCourseNameConfig,
    ManualCourseName: manualCourseNameConfig,
    CourseFee: courseFeeConfig,
    StartDate: startDateConfig,
    EndDate: endDateConfig,
    CASReceived: casReceivedConfig,
    IsWorkPlacement: isWorkPlacementConfig
  }: {
    [key: string]: ConfigField;
  } = config.Checklist.PersonalInfo.Page5;

  const visible: Visible = {
    studentNumber: studentNumberConfig?.visible,
    autoCourseName: autoCourseNameConfig?.visible && !courseNotListedOrResearchStudent,
    manualCourseName: manualCourseNameConfig?.visible && courseNotListedOrResearchStudent,
    startDate: startDateConfig?.visible,
    endDate: endDateConfig?.visible,
    courseFee: courseFeeConfig?.visible && courseNotListedOrResearchStudent,
    casReceived: casReceivedConfig?.visible,
    isWorkPlacement: isWorkPlacementConfig?.visible
  };

  const mandatory: Mandatory = {
    studentNumber: studentNumberConfig?.mandatory,
    autoCourseName: autoCourseNameConfig?.mandatory,
    manualCourseName: manualCourseNameConfig?.mandatory,
    startDate: startDateConfig?.mandatory,
    endDate: endDateConfig?.mandatory,
    courseFee: courseFeeConfig?.mandatory,
    casReceived: casReceivedConfig?.mandatory,
    isWorkPlacement: isWorkPlacementConfig?.mandatory
  };

  const isCourseComplete = (): boolean => {
    let isComplete = true;
    if (!courses || courses!.length === 0) {
      return false;
    }
    const parsedCourses = courses.filter(
      course => course && course.courseName !== '' && course.courseName !== undefined
    );

    parsedCourses!.forEach(course => {
      if (isComplete) {
        // prettier-ignore
        isComplete = (!visible.manualCourseName || !mandatory.manualCourseName || course.courseName !== '') &&
          (!visible.startDate || !mandatory.startDate || course.startDate !== '') &&
          (!visible.endDate || !mandatory.endDate || course.endDate !== '') &&
          (!visible.courseFee || !mandatory.courseFee || course.courseFee !== undefined) &&
          (!visible.isWorkPlacement || !mandatory.isWorkPlacement || course.isWorkPlacement !== undefined);
      }
    });

    return isComplete;
  };

  // prettier-ignore
  const complete =
    (!visible.studentNumber || !mandatory.studentNumber || studentNumber !== '') &&
    isCourseComplete() &&
    (!visible.casReceived || !mandatory.casReceived || casReceived !== undefined);

  return {
    complete,
    visible,
    mandatory
  };
}
